/**
 * LibraryView — Knowledge library.
 *
 * Surfaces every person the AI extracted from documents that isn't yet
 * linked to a tree member. Two lists side by side:
 *
 *   - Unmatched persons: grouped by normalised name. Each group shows the
 *     roles the person appeared in, birth year range, places, occupations,
 *     how many documents mentioned them. Click opens the first document
 *     (where the user can confirm or create).
 *
 *   - Recurring third parties: witnesses/officiants who appear on multiple
 *     documents and are almost certainly worth connecting — they're the
 *     family's neighbours, godparents, cousins.
 *
 * Everything is read-only here; the confirm/create workflow lives on the
 * DocCard entity chips. This view is for *discovery* — "who has the AI
 * been seeing that I've been ignoring?"
 */
import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'

export default function LibraryView({ treeId, onOpenDocument }) {
  const [entities, setEntities] = useState(null)
  const [witnesses, setWitnesses] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!treeId) return
    Promise.all([
      api.get(`/trees/${treeId}/kg/entities`).catch(err => { setError(err.message); return [] }),
      api.get(`/trees/${treeId}/kg/witnesses`).catch(() => []),
    ]).then(([e, w]) => {
      setEntities(e || [])
      setWitnesses(w || [])
    })
  }, [treeId])

  if (error) return <div className="form-error" style={{ margin: '2rem' }}>{error}</div>
  if (entities === null) return <div className="loading" style={{ margin: '3rem', textAlign: 'center' }}>Loading…</div>

  const hasEntities = entities.length > 0
  const hasWitnesses = witnesses && witnesses.length > 0

  if (!hasEntities && !hasWitnesses) {
    return (
      <div className="empty-state" style={{ padding: '3rem 1rem' }}>
        <div className="empty-state__icon">📚</div>
        <h3>Nothing in the library yet</h3>
        <p>
          Upload documents to a person and click <strong>Analyse with AI</strong>.
          Everyone the AI finds in those records who isn't yet linked to a tree
          member appears here — ready to review and connect.
        </p>
      </div>
    )
  }

  return (
    <div className="library-view">
      <div className="library-view__header">
        <div className="page-header__label">Knowledge library</div>
        <h2 className="library-view__title">Persons found in your documents</h2>
        <p className="library-view__hint">
          Names the AI has extracted from your documents that aren't yet linked to
          a person in this tree. Open a document to confirm or create.
        </p>
      </div>

      <div className="library-view__grid">
        <div className="library-view__col">
          <div className="library-view__col-title">
            Unmatched persons <span className="library-view__count">({entities.length})</span>
          </div>
          {entities.map(e => (
            <EntityCard key={e.name_normalized} entity={e} onOpenDocument={onOpenDocument} />
          ))}
        </div>

        {hasWitnesses && (
          <div className="library-view__col">
            <div className="library-view__col-title">
              Recurring witnesses <span className="library-view__count">({witnesses.length})</span>
            </div>
            <p className="library-view__col-sub">
              People who appear on several of your documents as witnesses or officiants —
              often neighbours, godparents, or unacknowledged relatives.
            </p>
            {witnesses.map(w => (
              <WitnessCard key={w.name_normalized} witness={w} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function EntityCard({ entity, onOpenDocument }) {
  const range = entity.birth_year_min && entity.birth_year_max
    ? entity.birth_year_min === entity.birth_year_max
      ? `b. ${entity.birth_year_min}`
      : `b. ${entity.birth_year_min}–${entity.birth_year_max}`
    : null
  const places = (entity.birth_places || []).filter(Boolean)
  const occs   = (entity.occupations  || []).filter(Boolean)
  const roles  = (entity.roles        || []).filter(Boolean)
  const firstDocId = entity.doc_ids?.[0]

  return (
    <div className="library-card">
      <div className="library-card__name">{entity.name_raw}</div>
      <div className="library-card__meta">
        {range && <span>{range}</span>}
        {places.length > 0 && <span>{places.join(' · ')}</span>}
        {occs.length > 0 && <span>{occs.join(' · ')}</span>}
      </div>
      {roles.length > 0 && (
        <div className="library-card__roles">
          {roles.map(r => <span key={r} className="library-card__role">{r}</span>)}
        </div>
      )}
      <div className="library-card__footer">
        <span className="library-card__mentions">
          {entity.mention_count} mention{entity.mention_count === '1' ? '' : 's'}
          {' · '}
          {entity.doc_ids.length} document{entity.doc_ids.length === 1 ? '' : 's'}
        </span>
        {entity.first_person_id && onOpenDocument && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onOpenDocument({ docId: entity.first_doc_id, personId: entity.first_person_id })}
          >
            Review →
          </button>
        )}
      </div>
    </div>
  )
}

function WitnessCard({ witness }) {
  const roles = (witness.roles || []).filter(Boolean)
  return (
    <div className="library-card library-card--witness">
      <div className="library-card__name">{witness.name_raw}</div>
      <div className="library-card__meta">
        <span>{witness.doc_count} documents</span>
        {witness.birth_year_est && <span>b. {witness.birth_year_est}</span>}
        {roles.length > 0 && <span>{roles.join(', ')}</span>}
      </div>
    </div>
  )
}
